import ChevronRight from '@mui/icons-material/ChevronRight';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import List from '@mui/material/List';
import { alpha, styled } from '@mui/material/styles';
import { Card } from '@qpr/components';
import React, { ReactNode } from 'react';
import ConditionalLink from './ConditionalLink';

const PREFIX = 'ListCard';

const classes = {
  card: `${PREFIX}-card`,
  cardContent: `${PREFIX}-cardContent`,
  cardHeader: `${PREFIX}-cardHeader`,
  headerContent: `${PREFIX}-headerContent`,
  list: `${PREFIX}-list`,
  avatar: `${PREFIX}-avatar`,
  linkStyle: `${PREFIX}-linkStyle`,
  linkIcon: `${PREFIX}-linkIcon`
};

const StyledCard = styled(Card)(({ theme }) => ({
  [`&.${classes.card}`]: {
    borderRadius: '4px',
    overflow: 'hidden',
    flexDirection: 'column',
    minHeight: '400px',
    height: '100%',
    margin: '0',
    padding: theme.spacing(1),
    boxShadow: 'none',
    background: `${alpha(theme.palette.background.default, 0.8)}`,
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: alpha(theme.palette.background.default, 0.8)
    }
  },

  [`& .${classes.cardContent}`]: {
    paddingTop: theme.spacing(0),
    paddingLeft: theme.spacing(0),
    paddingRight: '0px',
    height: '100%'
  },

  [`& .${classes.cardHeader}`]: {
    paddingLeft: '0px'
  },

  [`& .${classes.headerContent}`]: {
    paddingLeft: theme.spacing(2),
    color: alpha(theme.palette.grey[900], 0.9)
  },

  [`& .${classes.list}`]: {
    height: '100%'
  },

  [`& .${classes.avatar}`]: {
    paddingLeft: theme.spacing(1),
    color: theme.palette.primary.main
  },

  [`& .${classes.linkStyle}`]: {
    textDecoration: 'none',
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.11)
    }
  },

  [`& .${classes.linkIcon}`]: {
    color: theme.palette.primary.main,
    verticalAlign: 'middle',
    marginLeft: theme.spacing(1)
  }
}));

interface ListCardProps {
  title: string;
  avatar?: JSX.Element | string;
  link: string;
  children: ReactNode;
  tenantSymbol?: string;
  enabled: boolean;
}

const ListCard = ({ enabled, title, avatar, link, tenantSymbol = '', children }: ListCardProps) => {
  const resizedAvatar =
    typeof avatar === 'object' ? React.cloneElement(avatar, { style: { fontSize: '40px' } }) : null;

  return (
    <StyledCard className={classes.card}>
      <ConditionalLink
        enabled={enabled}
        link={link}
        tenantSymbol={tenantSymbol}
        customClass={classes.linkStyle}
      >
        <CardHeader
          title={
            <>
              {title}
              {enabled && link !== undefined && link !== '' && (
                <ChevronRight className={classes.linkIcon} />
              )}
            </>
          }
          titleTypographyProps={{ align: 'justify', variant: 'h6' }}
          avatar={resizedAvatar}
          data-testid="canvas-cell-header"
          classes={{
            root: classes.cardHeader,
            content: classes.headerContent,
            avatar: classes.avatar
          }}
        />
      </ConditionalLink>
      <CardContent className={classes.cardContent} data-testid="canvas-cell-content">
        <List className={classes.list}>{children}</List>
      </CardContent>
    </StyledCard>
  );
};

export default ListCard;
